<template>
  <div>
    <!-- Main Sidebar Item -->
    <div
      class="flex items-center justify-between p-2 rounded-md cursor-pointer transition-colors duration-200 hover:bg-[#2E3A46]"
      @click="toggleDropdown"
      :class="{
        'text-[#1294F0] bg-[#1294F0] bg-opacity-10': isActive,
        'text-[#A3B3C2]': !isActive,
      }"
    >
      <div class="flex items-center gap-3">
        <img
          :src="icon"
          alt=""
          class="w-5 h-5 transition-colors duration-200"
          :class="iconClass"
        />
        <span class="font-montserrat font-medium">{{ label }}</span>
      </div>

      <div class="flex items-center gap-2">
        <span
          v-if="badge"
          class="text-xs px-2 py-0.5 rounded-md"
          :class="{
            'bg-[#1294F0] text-white': badge === 'FREE',
            'bg-[#EEAF0E] text-black': badge !== 'FREE',
          }"
        >
          {{ badge }}
        </span>
        <svg
          v-if="hasDropdown"
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4 transition-transform duration-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          :class="{ 'rotate-180': open || isDropdownActive }"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
    </div>

    <!-- Smooth Transition -->
    <Transition
      enter-active-class="transition-all duration-300 ease-in-out"
      enter-from-class="opacity-0 max-h-0 scale-y-90"
      enter-to-class="opacity-100 max-h-40 scale-y-100"
      leave-active-class="transition-all duration-300 ease-in-out"
      leave-from-class="opacity-100 max-h-40 scale-y-100"
      leave-to-class="opacity-0 max-h-0 scale-y-90"
    >
      <div
        v-if="open || isDropdownActive"
        class="ml-8 mt-1 space-y-1 text-sm text-gray-300 overflow-hidden"
      >
        <div
          v-for="(item, index) in dropdown"
          :key="index"
          class="hover:text-white cursor-pointer px-2 py-1"
          @click="navigateTo(item)"
          :class="{
            'text-[#1294F0]': isDropdownItemActive(item) /* Only changes text color */,
          }"
        >
          - {{ item }}
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, defineProps } from "vue";
import { useRoute, useRouter } from "vue-router";

const props = defineProps({
  icon: String,
  label: String,
  badge: String,
  dropdown: Array,
  path: String,
});

const route = useRoute();
const router = useRouter();
const open = ref(false);

const hasDropdown = computed(() => props.dropdown && props.dropdown.length > 0);

const toggleDropdown = () => {
  if (hasDropdown.value) {
    open.value = !open.value;
  } else if (props.path) {
    router.push(props.path);
  }
};

const navigateTo = (subPath) => {
  if (props.label === "Leaderboards") {
    let type = subPath.toLowerCase().replace(/\s+/g, "");

    // Special cases
    if (subPath === "Rain.GG") type = "raingg";
    if (subPath === "Chicken.GG") type = "chickengg";

    router.push({ path: "/leaderboards", query: { type } });
  } else {
    const formattedPath = `/${subPath.toLowerCase().replace(/\s+/g, "-")}`;
    router.push(formattedPath);
  }
};


const isActive = computed(() => {
  return route.path === props.path || isDropdownActive.value;
});

const isDropdownActive = computed(() => {
  if (props.label === "Leaderboards") {
    return route.path === "/leaderboards" && route.query.type;
  }
  return props.dropdown?.some((item) => isDropdownItemActive(item));
});

const isDropdownItemActive = (item) => {
  if (props.label === "Leaderboards") {
    let type = item.toLowerCase().replace(/\s+/g, "");
    if (item === "Rain.GG") type = "raingg"; // special case
    return route.path === "/leaderboards" && route.query.type === type;
  }
  const formattedPath = `/${item.toLowerCase().replace(/\s+/g, "-")}`;
  return route.path === formattedPath;
};

</script>

<style scoped>
/* Change SVG color dynamically using CSS filters */
.filter-icon-active {
  filter: invert(44%) sepia(85%) saturate(394%) hue-rotate(184deg) brightness(94%)
    contrast(99%);
} /* Blue (#1294F0) */

.filter-icon-inactive {
  filter: invert(64%) sepia(7%) saturate(375%) hue-rotate(181deg) brightness(88%)
    contrast(89%);
} /* Gray (#A3B3C2) */
</style>